import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface Webhook {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  secret?: string;
  lastTriggered?: Date;
  failureCount: number;
}

const WebhookIntegrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    url: '',
    events: [] as string[],
    secret: '',
  });
  const [testing, setTesting] = useState<string | null>(null);

  const availableEvents = [
    { value: 'post.created', label: 'New Post Created' },
    { value: 'post.updated', label: 'Post Updated' },
    { value: 'post.deleted', label: 'Post Deleted' },
    { value: 'member.new', label: 'New Member' },
    { value: 'member.updated', label: 'Member Updated' },
    { value: 'member.cancelled', label: 'Member Cancelled' },
    { value: 'payment.success', label: 'Payment Success' },
    { value: 'payment.failed', label: 'Payment Failed' },
    { value: 'tier.created', label: 'New Tier Created' },
    { value: 'tier.updated', label: 'Tier Updated' },
  ];

  const handleEventToggle = (event: string) => {
    setNewWebhook({
      ...newWebhook,
      events: newWebhook.events.includes(event)
        ? newWebhook.events.filter(e => e !== event)
        : [...newWebhook.events, event],
    });
  };

  const createWebhook = async () => {
    if (!newWebhook.url || newWebhook.events.length === 0) {
      alert('Please provide webhook URL and select at least one event');
      return;
    }

    try {
      const response = await axios.post('/api/webhooks', newWebhook);
      setWebhooks([...webhooks, response.data]);
      setNewWebhook({ url: '', events: [], secret: '' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error creating webhook:', error);
      alert('Failed to create webhook');
    }
  };

  const deleteWebhook = async (id: string) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;

    try {
      await axios.delete(`/api/webhooks/${id}`);
      setWebhooks(webhooks.filter(w => w.id !== id));
    } catch (error) {
      console.error('Error deleting webhook:', error);
      alert('Failed to delete webhook');
    }
  };

  const toggleWebhook = async (id: string, active: boolean) => {
    try {
      await axios.patch(`/api/webhooks/${id}`, { active });
      setWebhooks(webhooks.map(w => w.id === id ? { ...w, active } : w));
    } catch (error) {
      console.error('Error toggling webhook:', error);
      alert('Failed to toggle webhook');
    }
  };

  const testWebhook = async (id: string) => {
    try {
      setTesting(id);
      await axios.post(`/api/webhooks/${id}/test`);
      alert('Test payload sent successfully!');
    } catch (error) {
      console.error('Error testing webhook:', error);
      alert('Failed to send test payload');
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard/integrations')}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Integrations
      </button>

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <span className="text-5xl">🔗</span>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Webhooks</h1>
            <p className="text-gray-600 mt-1">
              Send real-time events to your custom endpoints
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5" />
          Add Webhook
        </button>
      </div>

      {/* Add Webhook Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Webhook</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Webhook URL *
              </label>
              <input
                type="url"
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                placeholder="https://your-app.com/webhook"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Events to Subscribe *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {availableEvents.map((event) => (
                  <label key={event.value} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newWebhook.events.includes(event.value)}
                      onChange={() => handleEventToggle(event.value)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{event.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secret (Optional)
              </label>
              <input
                type="text"
                value={newWebhook.secret}
                onChange={(e) => setNewWebhook({ ...newWebhook, secret: e.target.value })}
                placeholder="Optional signing secret for verification"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Used to sign webhook payloads for verification
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={createWebhook}
                className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
              >
                Create Webhook
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Webhooks List */}
      {webhooks.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <span className="text-5xl mb-4 block">🔗</span>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Webhooks Yet</h3>
          <p className="text-gray-600 mb-6">
            Create your first webhook to start receiving real-time events
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
          >
            Create First Webhook
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <div
              key={webhook.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900 break-all">{webhook.url}</h3>
                    {webhook.active ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {webhook.events.map((event) => (
                      <span
                        key={event}
                        className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded"
                      >
                        {event}
                      </span>
                    ))}
                  </div>
                  {webhook.lastTriggered && (
                    <p className="text-xs text-gray-500">
                      Last triggered: {new Date(webhook.lastTriggered).toLocaleString()}
                    </p>
                  )}
                  {webhook.failureCount > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {webhook.failureCount} recent failures
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => toggleWebhook(webhook.id, !webhook.active)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                    webhook.active
                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      : 'bg-green-100 text-green-800 hover:bg-green-200'
                  }`}
                >
                  {webhook.active ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => testWebhook(webhook.id)}
                  disabled={testing === webhook.id}
                  className="px-3 py-1.5 text-sm font-medium bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 disabled:opacity-50"
                >
                  {testing === webhook.id ? 'Testing...' : 'Test'}
                </button>
                <button
                  onClick={() => deleteWebhook(webhook.id)}
                  className="px-3 py-1.5 text-sm font-medium bg-red-100 text-red-800 rounded-lg hover:bg-red-200 flex items-center gap-1"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Documentation */}
      <div className="mt-8 bg-blue-50 rounded-lg border border-blue-200 p-6">
        <h3 className="font-semibold text-blue-900 mb-3">Webhook Payload Format</h3>
        <pre className="bg-white p-4 rounded border border-blue-200 text-xs overflow-x-auto">
{`{
  "event": "post.created",
  "timestamp": "2025-12-01T12:00:00Z",
  "data": {
    "id": "post_123",
    "title": "New Post",
    "creatorId": "creator_456",
    ...
  }
}`}
        </pre>
        <p className="text-sm text-blue-800 mt-3">
          All webhook requests include an <code className="bg-blue-100 px-1 rounded">X-Webhook-Signature</code> header
          for verification when a secret is configured.
        </p>
      </div>
    </div>
  );
};

export default WebhookIntegrationPage;
